import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flag, Send, User, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  new: { label: "جديدة", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "قيد المتابعة", cls: "bg-blue-100 text-blue-800" },
  closed: { label: "مغلقة", cls: "bg-emerald-100 text-emerald-800" },
};

export default function Complaints() {
  const { user } = useAuth();
  return user.role === "student" ? <StudentComplaints user={user} /> : <AdminComplaints />;
}

function StudentComplaints({ user }) {
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", email: user.email || "", subject: "", body: "" });
  const [mine, setMine] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const load = () => api.get("/complaints/mine").then((r) => setMine(r.data)).catch(() => setMine([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("موضوع الشكوى مطلوب"); return; }
    setSaving(true);
    try { await api.post("/complaints", form); toast.success("تم إرسال الشكوى إلى المشرف العام"); setForm((f) => ({ ...f, subject: "", body: "" })); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (mine === null) return <PageLoader />;
  return (
    <>
      <PageHeader title="الشكاوى" subtitle="أرسل شكواك أو ملاحظتك مباشرة إلى المشرف العام" />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-100 bg-card p-6 soft-shadow">
          <div className="grid gap-4">
            <div><Label>الاسم</Label><Input data-testid="cmp-name" value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>رقم الهاتف</Label><Input data-testid="cmp-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
              <div><Label>البريد الإلكتروني</Label><Input data-testid="cmp-email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
            </div>
            <div><Label>موضوع الشكوى</Label><Input data-testid="cmp-subject" value={form.subject} onChange={(e) => set("subject")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
            <div><Label>التفاصيل</Label><Textarea data-testid="cmp-body" value={form.body} onChange={(e) => set("body")(e.target.value)} rows={5} className="mt-1.5 rounded-lg" /></div>
            <Button data-testid="cmp-submit" onClick={submit} disabled={saving || !form.subject.trim()} className="rounded-xl bg-greenx hover:bg-greenx-light"><Send className="me-2 h-4 w-4" /> إرسال الشكوى</Button>
          </div>
        </div>
        <div>
          <h3 className="mb-3 font-heading text-lg font-bold text-navy">شكاواي السابقة</h3>
          {mine.length === 0 ? <EmptyState icon={Flag} title="لا توجد شكاوى" desc="لم ترسل أي شكوى بعد." /> : (
            <div className="space-y-3">
              {mine.map((c) => (
                <div key={c.id} data-testid={`mine-${c.id}`} className="rounded-xl border border-slate-100 bg-white p-4 soft-shadow">
                  <div className="flex items-center justify-between"><p className="font-semibold text-navy">{c.subject}</p><span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS[c.status]?.cls}`}>{STATUS[c.status]?.label}</span></div>
                  <p className="mt-1 text-sm text-slate-500">{c.body || "—"}</p>
                  {c.reply && <div data-testid={`reply-${c.id}`} className="mt-2 rounded-lg bg-emerald-50 p-3 text-sm"><span className="font-semibold text-emerald-700">رد المشرف: </span><span className="text-slate-700">{c.reply}</span></div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ReplyBox({ complaint, onDone }) {
  const [text, setText] = useState(complaint.reply || "");
  const [saving, setSaving] = useState(false);
  const send = async () => {
    if (!text.trim()) { toast.error("اكتب نص الرد"); return; }
    setSaving(true);
    try { await api.put(`/complaints/${complaint.id}/reply`, { reply: text.trim() }); toast.success("تم إرسال الرد للطالب"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      {complaint.reply && <div className="mb-2 rounded-lg bg-emerald-50 p-2.5 text-sm"><span className="font-semibold text-emerald-700">ردك الحالي: </span>{complaint.reply}</div>}
      <div className="flex items-end gap-2">
        <Textarea data-testid={`reply-input-${complaint.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="اكتب ردًا يظهر للطالب..." className="rounded-lg" />
        <Button data-testid={`reply-send-${complaint.id}`} onClick={send} disabled={saving || !text.trim()} className="rounded-xl bg-greenx hover:bg-greenx-light"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function AdminComplaints() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/complaints").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/complaints/${id}/status`, { status }); toast.success("تم تحديث الحالة"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (items === null) return <PageLoader />;
  return (
    <>
      <PageHeader title="الشكاوى" subtitle="متابعة شكاوى الطلاب" />
      {items.length === 0 ? <EmptyState icon={Flag} title="لا توجد شكاوى" /> : (
        <div className="space-y-4">
          {items.map((c) => (
            <div key={c.id} data-testid={`complaint-${c.id}`} className="rounded-2xl border border-slate-100 bg-white p-5 soft-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-bold text-navy">{c.subject}</h3>
                  <p className="mt-1 text-sm text-slate-600">{c.body || "—"}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {c.name}</span>
                    {c.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.phone}</span>}
                    {c.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {c.email}</span>}
                  </div>
                </div>
                <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                  <SelectTrigger data-testid={`status-select-${c.id}`} className="w-40 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">جديدة</SelectItem>
                    <SelectItem value="in_progress">قيد المتابعة</SelectItem>
                    <SelectItem value="closed">مغلقة</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ReplyBox complaint={c} onDone={load} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
